import { FC, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMicrophone, faPaperPlane, faXmark } from '@fortawesome/free-solid-svg-icons';
import { VoiceRecording, formatVoiceTime, useVoiceRecorder } from './voiceRecorder';

interface AssistantVoiceButtonProps {
  disabled: boolean;
  maxSeconds: number;
  onRecorded: (recording: VoiceRecording) => void;
  onRecordingChange: (recording: boolean) => void;
  onError: (kind: 'denied' | 'unsupported') => void;
}

/**
 * The microphone in the chat's composer: tap to record, the paper plane to send, the X to cancel. While recording it
 * shows the time. The finished recording goes to the parent's *latest* onRecorded: the page may have changed while the
 * user was talking, and the send path reads the current page.
 */
const AssistantVoiceButton: FC<AssistantVoiceButtonProps> = ({
  disabled,
  maxSeconds,
  onRecorded,
  onRecordingChange,
  onError,
}) => {
  const { t } = useTranslation();
  const { recording, elapsedMs, start, stop, cancel } = useVoiceRecorder(maxSeconds);
  const onRecordedRef = useRef(onRecorded);
  onRecordedRef.current = onRecorded;
  const cancelRef = useRef(cancel);
  cancelRef.current = cancel;

  useEffect(() => {
    onRecordingChange(recording);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recording]);

  // Leaving the page, or the panel remounting, while recording drops the recording and frees the microphone.
  useEffect(() => () => cancelRef.current(), []);

  const begin = async () => {
    try {
      await start((finished) => {
        if (finished) {
          onRecordedRef.current(finished);
        }
      });
    } catch (err) {
      onError((err as DOMException)?.name === 'NotAllowedError' ? 'denied' : 'unsupported');
    }
  };

  if (recording) {
    return (
      <span className="assistant-chat-recording" role="status" aria-label={t('assistant.voice.recording')}>
        <span className="assistant-chat-rec-dot" />
        <span className="assistant-chat-rec-time">{formatVoiceTime(elapsedMs)}</span>
        <button type="button" className="assistant-chat-rec-btn" title={t('assistant.voice.cancel')} onClick={cancel}>
          <FontAwesomeIcon icon={faXmark} />
        </button>
        <button
          type="button"
          className="btn btn-primary assistant-chat-rec-btn"
          title={t('assistant.voice.stop')}
          onClick={stop}
        >
          <FontAwesomeIcon icon={faPaperPlane} />
        </button>
      </span>
    );
  }

  return (
    <button
      type="button"
      className="assistant-chat-mic"
      title={t('assistant.voice.record')}
      disabled={disabled}
      onClick={begin}
    >
      <FontAwesomeIcon icon={faMicrophone} />
    </button>
  );
};

export default AssistantVoiceButton;
